/**
 * Language configuration loader
 * Single source of truth for all language-related configuration
 */

import languageConfig from './language-config.json';

export interface LanguageDefinition {
  code: string;
  locale: string;
  name: string;
  enabled: boolean;
}

export interface LanguageConfig {
  defaultLanguage: string;
  defaultLocale: string;
  languages: LanguageDefinition[];
  debugLanguage?: LanguageDefinition;
}

/**
 * Typed language configuration loaded from JSON
 */
export const LANGUAGE_CONFIG: LanguageConfig = languageConfig as LanguageConfig;

/**
 * List of all enabled language codes
 */
export const ALL_LANGUAGES_LIST: string[] = LANGUAGE_CONFIG.languages
  .filter((lang) => lang.enabled)
  .map((lang) => lang.code);

/**
 * Mapping from language codes to their corresponding locale identifiers
 */
export const LANGUAGE_TO_LOCALE_MAP: Record<string, string> = Object.fromEntries([
  ...LANGUAGE_CONFIG.languages.map((lang) => [lang.code, lang.locale]),
  ...(LANGUAGE_CONFIG.debugLanguage
    ? [[LANGUAGE_CONFIG.debugLanguage.code, LANGUAGE_CONFIG.debugLanguage.locale]]
    : []),
]);

/**
 * Default language code
 */
export const DEFAULT_LANGUAGE = LANGUAGE_CONFIG.defaultLanguage;

/**
 * Default locale
 */
export const DEFAULT_LOCALE = LANGUAGE_CONFIG.defaultLocale;

/**
 * Get language definition by code
 */
export function getLanguageByCode(code: string): LanguageDefinition | undefined {
  return (
    LANGUAGE_CONFIG.languages.find((lang) => lang.code === code) ||
    (LANGUAGE_CONFIG.debugLanguage?.code === code ? LANGUAGE_CONFIG.debugLanguage : undefined)
  );
}

/**
 * Check if a language is enabled
 */
export function isLanguageEnabled(code: string): boolean {
  const lang = getLanguageByCode(code);
  return lang?.enabled ?? false;
}

/**
 * Get all enabled languages
 */
export function getEnabledLanguages(): LanguageDefinition[] {
  return LANGUAGE_CONFIG.languages.filter((lang) => lang.enabled);
}
